import { create } from 'zustand';
import { PaginationType } from '../types/pagination';
import {
  FetchFormsRequest,
  FormElementType,
  FormType,
  deleteForm,
  fetchForm,
  fetchFormList,
  postForm,
  putForm,
  restoreForm,
} from './api/forms-api';
import { useAlertStore } from './use-alert-store';

interface Store extends Partial<PaginationType> {
  fetchFormList: (data: FetchFormsRequest) => Promise<void>;
  fetchForm: (id: string) => Promise<void>;
  postForm: () => Promise<void>;
  putForm: () => Promise<void>;
  deleteForm: (id: string) => Promise<void>;
  restoreForm: (id: string) => Promise<void>;
  isLoadingFetch?: boolean;
  isLoading?: boolean;
  list?: FormType[];
  form?: Partial<FormType>;
  newElement?: Partial<FormElementType>;
  handleUpdateNewElement: (data: Partial<FormElementType>) => void;
  handleResetNewElement: () => void;
  handleResetForm: () => void;
  handleUpdateForm: (data: Partial<FormType>) => void;
}

export const useFormStore = create<Store>((set, get) => ({
  handleResetNewElement() {
    set((store) => ({
      ...store,
      newElement: undefined,
    }));
  },
  handleUpdateNewElement(data) {
    set((store) => ({
      ...store,
      newElement: {
        ...store.newElement,
        ...data,
      },
    }));
  },
  handleUpdateForm: (data) => {
    set((store) => ({
      ...store,
      form: {
        ...store.form,
        ...data,
      },
    }));
  },
  handleResetForm: async () => {
    try {
      set({ form: undefined });
    } catch (e) {
      console.error(e);
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  fetchFormList: async (params) => {
    set({ isLoadingFetch: true });
    try {
      const resp = await fetchFormList(params);

      set({ list: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по формам', variant: 'danger' });
    } finally {
      set({ isLoadingFetch: false });
    }
  },
  fetchForm: async (id: string) => {
    set({ isLoading: true });
    try {
      const resp = await fetchForm(id);
      set({ form: resp });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось получить данные по форме', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  postForm: async () => {
    set({ isLoading: true });
    try {
      const form = get().form;

      if (!form) {
        throw new Error('Form is not defined');
      }
      const withoutTempUuid = form.elements?.filter((item) => item.uuid?.startsWith('temp-')) || [];
      await postForm({...form, elements: withoutTempUuid});
      useAlertStore.getState().add({ label: 'Форма успешно создана', variant: 'success' });
      set({ form: undefined });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по форме', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  putForm: async () => {
    set({ isLoading: true });
    try {
      const form = get().form;

      if (!form) {
        throw new Error('Form is not defined');
      }
      await putForm(form);
      useAlertStore.getState().add({ label: 'Форма успешно обновлена', variant: 'success' });
      set({ form: undefined });
    } catch (e) {
      console.error(e);
      useAlertStore
        .getState()
        .add({ label: 'Не удалось сохранить данные по форме', variant: 'danger' });
      throw e;
    } finally {
      set({ isLoading: false });
    }
  },
  deleteForm: async (id) => {
    set({ isLoading: true });
    try {
      await deleteForm(id);
      useAlertStore.getState().add({ label: 'Форма успешно удалена', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.uuid) === id ? { ...item, is_deleted: true } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось удалить форму', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
  restoreForm: async (id) => {
    set({ isLoading: true });
    try {
      await restoreForm(id);
      useAlertStore.getState().add({ label: 'Форма успешно восстановлена', variant: 'success' });
      set((store) => ({
        ...store,
        list: store.list?.map((item) =>
          String(item.uuid) === id ? { ...item, is_deleted: false } : item,
        ),
      }));
    } catch (e) {
      console.error(e);
      useAlertStore.getState().add({ label: 'Не удалось восстановить форму', variant: 'danger' });
    } finally {
      set({ isLoading: false });
    }
  },
}));
